import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search,
  User,
  Handshake,
  Building2,
  Phone,
  Mail,
  Plus,
  X,
  PhoneCall,
  Loader2,
  Copy,
  Check,
  Briefcase,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useGlobalSearch } from "./useGlobalSearch";
import { useCRM } from "../context/CRMContext";
import { useVoip } from "../voip/VoipContext";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { findDealLabel } from "../deals/dealUtils";
import { normalizeUnicodeFancyName } from "../deals/dealSortUtils";
import { cn } from "@/lib/utils";

interface MatchedContact {
  id: string | number;
  first_name: string;
  last_name?: string;
  title?: string;
  company_id?: string | number | null;
  company_name?: string;
  phones: Array<{ number: string; type: string }>;
  emails: Array<{ email: string; type: string }>;
  deals: MatchedDeal[];
  matchedField?: string;
}

interface MatchedDeal {
  id: string | number;
  name: string;
  amount?: number;
  stage?: string;
  status?: string;
  company_id?: string | number | null;
  company_name?: string;
  contact_ids?: Array<string | number>;
  contacts?: Array<{ id: string | number; name: string }>;
  created_at?: string;
}

interface MatchedCompany {
  id: string | number;
  name: string;
  phone_number?: string;
  website?: string;
  city?: string;
  state_abbr?: string;
  sector?: string;
  contacts: MatchedContact[];
  deals: MatchedDeal[];
}

const normalizeSearchText = (str?: string | null): string =>
  normalizeUnicodeFancyName(str)
    .replace(/^0(?=[a-zA-Z])/, "o")
    .toLowerCase();

const levenshteinDistance = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0),
  );
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  return dp[m][n];
};

const computeTextMatch = (
  target?: string | null,
  rawQuery?: string | null,
): { score: number; isFuzzy: boolean } => {
  const normTarget = normalizeSearchText(target);
  const normQuery = normalizeSearchText(rawQuery);
  if (!normTarget || !normQuery) return { score: 0, isFuzzy: false };

  if (normTarget === normQuery) return { score: 100, isFuzzy: false };
  if (normTarget.startsWith(normQuery)) return { score: 85, isFuzzy: false };
  if (normTarget.includes(normQuery)) return { score: 70, isFuzzy: false };

  const qWords = normQuery.split(/\s+/).filter(Boolean);
  const tWords = normTarget.split(/\s+/).filter(Boolean);

  if (qWords.length > 1 && qWords.every((qw) => normTarget.includes(qw))) {
    return { score: 65, isFuzzy: false };
  }

  // Tolerância a erros de digitação / letras parecidas (ex: "Darian" -> "Darlan", "Camilla" -> "Camila")
  let allWordsMatched = true;
  let bestScore = 0;
  let usedFuzzy = false;

  for (const qw of qWords) {
    if (qw.length < 3) {
      if (!normTarget.includes(qw)) {
        allWordsMatched = false;
        break;
      }
      bestScore = Math.max(bestScore, 55);
      continue;
    }

    let wordMatched = false;
    for (const tw of tWords) {
      if (tw.includes(qw)) {
        wordMatched = true;
        bestScore = Math.max(bestScore, 60);
        break;
      }

      if (qw.length >= 4 && tw.length >= 3) {
        const sameStart =
          qw[0] === tw[0] ||
          (qw.length >= 4 &&
            tw.length >= 4 &&
            qw[0] === tw[1] &&
            qw[1] === tw[0]);

        if (sameStart) {
          const prefixTw = tw.length > qw.length ? tw.slice(0, qw.length) : tw;
          const dist = Math.min(
            levenshteinDistance(qw, tw),
            levenshteinDistance(qw, prefixTw),
          );
          const maxDist = qw.length >= 6 ? 2 : 1;

          if (dist <= maxDist) {
            wordMatched = true;
            usedFuzzy = true;
            bestScore = Math.max(bestScore, dist === 1 ? 52 : 44);
            break;
          }
        }
      }
    }

    if (!wordMatched) {
      allWordsMatched = false;
      break;
    }
  }

  return allWordsMatched
    ? { score: bestScore, isFuzzy: usedFuzzy }
    : { score: 0, isFuzzy: false };
};

export const GlobalSearchDialog: React.FC = () => {
  const { isOpen, closeSearch, searchQuery } = useGlobalSearch();
  const { currentCrm, isVoipEnabled, isSuperadmin } = useCRM();
  const { isConnected, openLoginModal, makeCall } = useVoip();
  const { dealStages, currency } = useConfigurationContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState(searchQuery || "");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState<
    "all" | "contacts" | "deals" | "companies"
  >("all");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const [contacts, setContacts] = useState<MatchedContact[]>([]);
  const [standaloneDeals, setStandaloneDeals] = useState<MatchedDeal[]>([]);
  const [companies, setCompanies] = useState<MatchedCompany[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const crmCacheRef = useRef<{
    crmId: string;
    deals: any[];
    contacts: any[];
    companies: any[];
    promise?: Promise<{ deals: any[]; contacts: any[]; companies: any[] }>;
  } | null>(null);

  const resolveActiveCrmId = (): string | null => {
    if (typeof window !== "undefined" && window.localStorage) {
      const stored = window.localStorage.getItem("dotus_active_crm_id");
      if (stored && stored !== "00000000-0000-0000-0000-000000000001") {
        return stored;
      }
    }
    if (currentCrm?.id) return currentCrm.id;
    return null;
  };

  // Extrai registros já renderizados na tela (cache do React Query desta aba)
  const extractOnScreenCache = () => {
    const cachedDeals: any[] = [];
    const cachedContacts: any[] = [];
    const cachedCompanies: any[] = [];

    try {
      const allQueries = queryClient.getQueriesData<any>({});
      for (const [queryKey, data] of allQueries) {
        if (!data || !Array.isArray(queryKey) || queryKey.length === 0)
          continue;
        const resource = String(queryKey[0]);
        const rows: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : data?.id
              ? [data]
              : [];

        if (rows.length === 0) continue;

        if (resource === "deals") {
          cachedDeals.push(...rows);
        } else if (resource === "contacts" || resource === "contacts_summary") {
          cachedContacts.push(...rows);
        } else if (
          resource === "companies" ||
          resource === "companies_summary"
        ) {
          cachedCompanies.push(...rows);
        }
      }
    } catch {
      // ignora falhas de leitura do cache
    }

    return { cachedDeals, cachedContacts, cachedCompanies };
  };

  const ensureBasePool = () => {
    const crmId = resolveActiveCrmId() || "all";
    if (!crmCacheRef.current || crmCacheRef.current.crmId !== crmId) {
      const supabase = getSupabaseClient();
      const loadBasePool = async () => {
        const dq = supabase
          .from("deals")
          .select(
            "id, crm_id, name, description, amount, stage, status, contact_ids, company_id, created_at, custom_fields",
          )
          .order("id", { ascending: false })
          .limit(3000);
        const cq = supabase
          .from("contacts")
          .select(
            "id, crm_id, first_name, last_name, title, company_id, phone_jsonb, email_jsonb, custom_fields",
          )
          .order("id", { ascending: false })
          .limit(3000);
        const compQ = supabase
          .from("companies")
          .select(
            "id, crm_id, name, phone_number, website, city, state_abbr, sector",
          )
          .order("id", { ascending: false })
          .limit(1500);

        const [dRes, cRes, compRes] = await Promise.all([dq, cq, compQ]);
        const result = {
          deals: dRes.data || [],
          contacts: cRes.data || [],
          companies: compRes.data || [],
        };
        crmCacheRef.current = {
          crmId,
          ...result,
        };
        return result;
      };

      crmCacheRef.current = {
        crmId,
        deals: [],
        contacts: [],
        companies: [],
        promise: loadBasePool(),
      };
    }
    return crmCacheRef.current;
  };

  // Debounce rápido da digitação (120ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 120);
    return () => clearTimeout(timer);
  }, [query]);

  // Pré-carrega base do CRM ao abrir o modal para busca instantânea, sem acentos e tolerante a erros
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 60);
      ensureBasePool();
    } else {
      setQuery("");
      setDebouncedQuery("");
      setContacts([]);
      setStandaloneDeals([]);
      setCompanies([]);
      setActiveTab("all");
      crmCacheRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentCrm?.id]);

  // Executa busca relacional híbrida (Cache de Tela + Pool em memória + SQL direto + Fuzzy + Sem Acentos)
  useEffect(() => {
    if (!isOpen) return;

    const rawTerm = debouncedQuery.trim();
    if (rawTerm.length < 2) {
      setContacts([]);
      setStandaloneDeals([]);
      setCompanies([]);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const executeSearch = async () => {
      setIsLoading(true);
      const supabase = getSupabaseClient();
      const termLower = rawTerm.toLowerCase();
      const normTerm = normalizeSearchText(rawTerm);
      const cleanTerm = rawTerm.replace(/[,()'"\\]/g, " ").trim();
      const digitsOnly = rawTerm.replace(/\D/g, "");
      const words = cleanTerm.split(/\s+/).filter((w) => w.length >= 2);

      try {
        const activeCrmId = resolveActiveCrmId();
        const preferredCrmIds = new Set<string>();
        if (activeCrmId) preferredCrmIds.add(activeCrmId);
        if (currentCrm?.id) preferredCrmIds.add(currentCrm.id);

        // 0. Extrai imediatamente tudo que já está na tela (Kanban / listas abertas nesta aba)
        const { cachedDeals, cachedContacts, cachedCompanies } =
          extractOnScreenCache();
        cachedDeals.forEach((d) => {
          if (d?.crm_id) preferredCrmIds.add(String(d.crm_id));
        });

        // Garante que o pool base do CRM está carregado (mesmo após HMR com modal aberto)
        const cacheEntry = ensureBasePool();
        let baseDeals: any[] = [];
        let baseContacts: any[] = [];
        let baseCompanies: any[] = [];

        if (cacheEntry.promise) {
          const loaded = await cacheEntry.promise;
          baseDeals = loaded.deals;
          baseContacts = loaded.contacts;
          baseCompanies = loaded.companies;
        } else {
          baseDeals = cacheEntry.deals;
          baseContacts = cacheEntry.contacts;
          baseCompanies = cacheEntry.companies;
        }

        // Em paralelo, caso a base tenha >3000 registros, faz busca pontual SQL para complementar
        const dealOrConds: string[] = [
          `name.ilike.%${cleanTerm}%`,
          `description.ilike.%${cleanTerm}%`,
        ];
        if (words.length > 1) {
          words.forEach((w) => dealOrConds.push(`name.ilike.%${w}%`));
        }
        const dealsSqlQuery = supabase
          .from("deals")
          .select(
            "id, crm_id, name, description, amount, stage, status, contact_ids, company_id, created_at, custom_fields",
          )
          .or(dealOrConds.join(","));

        const contactOrConds: string[] = [
          `first_name.ilike.%${cleanTerm}%`,
          `last_name.ilike.%${cleanTerm}%`,
          `title.ilike.%${cleanTerm}%`,
        ];
        if (words.length > 1) {
          words.forEach((w) => {
            contactOrConds.push(`first_name.ilike.%${w}%`);
            contactOrConds.push(`last_name.ilike.%${w}%`);
          });
        }
        const contactsSqlQuery = supabase
          .from("contacts")
          .select(
            "id, crm_id, first_name, last_name, title, company_id, phone_jsonb, email_jsonb, custom_fields",
          )
          .or(contactOrConds.join(","));

        const [sqlDealsRes, sqlContactsRes] = await Promise.all([
          dealsSqlQuery.limit(80),
          contactsSqlQuery.limit(80),
        ]);

        if (isCancelled) return;

        // Mescla cache da tela + pool em memória + resultados SQL sem duplicatas
        const dealsById = new Map<string, any>();
        cachedDeals.forEach((d) => {
          if (d?.id != null) dealsById.set(String(d.id), d);
        });
        baseDeals.forEach((d) => {
          if (d?.id != null) dealsById.set(String(d.id), d);
        });
        (sqlDealsRes.data || []).forEach((d) => {
          if (d?.id != null) dealsById.set(String(d.id), d);
        });
        const allDealsCombined = Array.from(dealsById.values());

        const contactsById = new Map<string, any>();
        cachedContacts.forEach((c) => {
          if (c?.id != null) contactsById.set(String(c.id), c);
        });
        baseContacts.forEach((c) => {
          if (c?.id != null) contactsById.set(String(c.id), c);
        });
        (sqlContactsRes.data || []).forEach((c) => {
          if (c?.id != null) contactsById.set(String(c.id), c);
        });
        const allContactsCombined = Array.from(contactsById.values());

        const companiesById = new Map<string, any>();
        cachedCompanies.forEach((comp) => {
          if (comp?.id != null) companiesById.set(String(comp.id), comp);
        });
        baseCompanies.forEach((comp) => {
          if (comp?.id != null) companiesById.set(String(comp.id), comp);
        });
        const allCompaniesCombined = Array.from(companiesById.values());

        const companyMap = new Map<string, string>();
        allCompaniesCombined.forEach((comp) => {
          companyMap.set(String(comp.id), comp.name || "Empresa");
        });

        // Mapeia todos os negócios do CRM
        const dealMap = new Map<string, MatchedDeal>();
        allDealsCombined.forEach((deal) => {
          const dealIdStr = String(deal.id);
          const compName = deal.company_id
            ? companyMap.get(String(deal.company_id))
            : undefined;

          dealMap.set(dealIdStr, {
            id: deal.id,
            name: deal.name || "Negócio sem título",
            amount: deal.amount || 0,
            stage: deal.stage,
            status: deal.status,
            company_id: deal.company_id,
            company_name: compName,
            contact_ids: deal.contact_ids,
            created_at: deal.created_at,
          });
        });

        // 1. Avalia Negócios (Diretos + Fuzzy + Sem Acento)
        const directDealsList: (MatchedDeal & { _score: number })[] = [];
        const directlyMatchedDealContactIds = new Map<string, number>();

        allDealsCombined.forEach((deal) => {
          const nameMatch = computeTextMatch(deal.name, rawTerm);
          const descMatch =
            nameMatch.score === 0 && deal.description
              ? computeTextMatch(deal.description, rawTerm)
              : { score: 0, isFuzzy: false };

          const baseDealScore = Math.max(
            nameMatch.score,
            descMatch.score > 0 ? Math.min(descMatch.score, 55) : 0,
          );

          if (baseDealScore > 0) {
            const crmBoost =
              deal.crm_id && preferredCrmIds.has(String(deal.crm_id)) ? 10 : 0;
            const bestDealScore = baseDealScore + crmBoost;
            const compName = deal.company_id
              ? companyMap.get(String(deal.company_id))
              : undefined;

            directDealsList.push({
              id: deal.id,
              name: deal.name || "Negócio sem título",
              amount: deal.amount || 0,
              stage: deal.stage,
              status: deal.status,
              company_id: deal.company_id,
              company_name: compName,
              contact_ids: deal.contact_ids,
              created_at: deal.created_at,
              _score: bestDealScore,
            });

            (deal.contact_ids || []).forEach((cid: any) => {
              const cidStr = String(cid);
              const prev = directlyMatchedDealContactIds.get(cidStr) || 0;
              if (bestDealScore > prev) {
                directlyMatchedDealContactIds.set(cidStr, bestDealScore);
              }
            });
          }
        });

        directDealsList.sort((a, b) => b._score - a._score);

        // 2. Avalia Contatos (Nome, Cargo, E-mail, Telefone, Empresa, Custom Fields e Vínculo com Negócio)
        const matchedContactsList: (MatchedContact & { _score: number })[] = [];

        allContactsCombined.forEach((c) => {
          const fullName = `${c.first_name || ""} ${c.last_name || ""}`.trim();
          const compName = c.company_id
            ? companyMap.get(String(c.company_id)) || ""
            : "";

          // Extrai telefones
          const extractedPhones: Array<{ number: string; type: string }> = [];
          if (Array.isArray(c.phone_jsonb)) {
            c.phone_jsonb.forEach((item: any) => {
              if (typeof item === "string" && item.trim()) {
                extractedPhones.push({ number: item.trim(), type: "Telefone" });
              } else if (item && typeof item === "object") {
                const num = item.number || item.phone || item.telefone;
                if (num && typeof num === "string" && num.trim()) {
                  extractedPhones.push({
                    number: num.trim(),
                    type: item.type || item.tipo || "Telefone",
                  });
                }
              }
            });
          }

          // Extrai emails
          const extractedEmails: Array<{ email: string; type: string }> = [];
          if (Array.isArray(c.email_jsonb)) {
            c.email_jsonb.forEach((item: any) => {
              if (typeof item === "string" && item.trim()) {
                extractedEmails.push({ email: item.trim(), type: "E-mail" });
              } else if (item && typeof item === "object") {
                const em = item.email || item.mail;
                if (em && typeof em === "string" && em.trim()) {
                  extractedEmails.push({
                    email: em.trim(),
                    type: item.type || item.tipo || "E-mail",
                  });
                }
              }
            });
          }

          let matched = false;
          let matchedField = "";
          let score = 0;

          // A) Match no nome (exato, parcial, sem acento ou fuzzy como "Darian" -> "Darlan")
          const nameMatch = computeTextMatch(fullName, rawTerm);
          if (nameMatch.score > 0) {
            matched = true;
            matchedField = nameMatch.isFuzzy ? "Nome aproximado" : "Nome";
            score = nameMatch.score;
          }

          // B) Match no cargo
          if (!matched && c.title) {
            const titleMatch = computeTextMatch(c.title, rawTerm);
            if (titleMatch.score > 0) {
              matched = true;
              matchedField = "Cargo";
              score = Math.min(titleMatch.score, 60);
            }
          }

          // C) Match no email
          if (!matched) {
            const hasEmailMatch = extractedEmails.some((e) =>
              normalizeSearchText(e.email).includes(normTerm),
            );
            if (hasEmailMatch) {
              matched = true;
              matchedField = "E-mail";
              score = 75;
            }
          }

          // D) Match no telefone
          if (!matched) {
            const hasPhoneMatch = extractedPhones.some((p) => {
              const rawNumLower = p.number.toLowerCase();
              if (rawNumLower.includes(termLower)) return true;
              if (digitsOnly.length >= 3) {
                const pDigits = p.number.replace(/\D/g, "");
                return pDigits.includes(digitsOnly);
              }
              return false;
            });

            if (hasPhoneMatch) {
              matched = true;
              matchedField = "Telefone";
              score = 80;
            }
          }

          // E) Match na empresa vinculada
          if (!matched && compName) {
            const compMatch = computeTextMatch(compName, rawTerm);
            if (compMatch.score >= 60) {
              matched = true;
              matchedField = "Empresa";
              score = 50;
            }
          }

          // F) Match em custom_fields
          if (
            !matched &&
            c.custom_fields &&
            typeof c.custom_fields === "object"
          ) {
            const cfValues = normalizeSearchText(
              Object.values(c.custom_fields).join(" "),
            );
            if (cfValues.includes(normTerm)) {
              matched = true;
              matchedField = "Dados Adicionais";
              score = 45;
            }
          }

          // G) Match via negócio vinculado encontrado
          const contactIdStr = String(c.id);
          if (!matched && directlyMatchedDealContactIds.has(contactIdStr)) {
            matched = true;
            matchedField = "Negócio Vinculado";
            score = (directlyMatchedDealContactIds.get(contactIdStr) || 55) - 5;
          }

          if (matched) {
            const crmBoost =
              c.crm_id && preferredCrmIds.has(String(c.crm_id)) ? 10 : 0;
            const connectedDealsForContact: MatchedDeal[] = [];
            dealMap.forEach((deal) => {
              const cIds = (deal.contact_ids || []).map(String);
              if (cIds.includes(contactIdStr)) {
                connectedDealsForContact.push(deal);
              }
            });

            matchedContactsList.push({
              id: c.id,
              first_name: c.first_name,
              last_name: c.last_name,
              title: c.title,
              company_id: c.company_id,
              company_name: compName || undefined,
              phones: extractedPhones,
              emails: extractedEmails,
              deals: connectedDealsForContact,
              matchedField,
              _score: score + crmBoost,
            });
          }
        });

        matchedContactsList.sort((a, b) => b._score - a._score);

        // 3. Avalia Empresas
        const matchedCompaniesList: (MatchedCompany & { _score: number })[] =
          [];
        allCompaniesCombined.forEach((comp) => {
          const compPhone = comp.phone_number || "";
          const compWeb = normalizeSearchText(comp.website);
          const compSector = normalizeSearchText(comp.sector);

          let compMatched = false;
          let compScore = 0;

          const nameMatch = computeTextMatch(comp.name, rawTerm);
          if (nameMatch.score > 0) {
            compMatched = true;
            compScore = nameMatch.score;
          } else if (compWeb && compWeb.includes(normTerm)) {
            compMatched = true;
            compScore = 55;
          } else if (compSector && compSector.includes(normTerm)) {
            compMatched = true;
            compScore = 45;
          } else if (
            digitsOnly.length >= 3 &&
            compPhone.replace(/\D/g, "").includes(digitsOnly)
          ) {
            compMatched = true;
            compScore = 70;
          }

          if (compMatched) {
            const crmBoost =
              comp.crm_id && preferredCrmIds.has(String(comp.crm_id)) ? 10 : 0;
            const compIdStr = String(comp.id);
            const compContacts = matchedContactsList.filter(
              (c) => String(c.company_id) === compIdStr,
            );
            const compDeals: MatchedDeal[] = [];
            dealMap.forEach((d) => {
              if (String(d.company_id) === compIdStr) {
                compDeals.push(d);
              }
            });

            matchedCompaniesList.push({
              id: comp.id,
              name: comp.name || "Empresa",
              phone_number: comp.phone_number,
              website: comp.website,
              city: comp.city,
              state_abbr: comp.state_abbr,
              sector: comp.sector,
              contacts: compContacts,
              deals: compDeals,
              _score: compScore + crmBoost,
            });
          }
        });

        matchedCompaniesList.sort((a, b) => b._score - a._score);

        setContacts(matchedContactsList.slice(0, 50));
        setCompanies(matchedCompaniesList.slice(0, 40));
        setStandaloneDeals(directDealsList.slice(0, 60));
      } catch (err) {
        console.error("[GlobalSearch] Erro ao pesquisar no CRM:", err);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    executeSearch();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, isOpen, currentCrm?.id]);

  const handleCopy = (text: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    toast.success("Copiado para a área de transferência!");
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleCall = async (
    phone: string,
    contactName: string,
    dealId?: string | number,
    e?: React.MouseEvent,
  ) => {
    e?.stopPropagation();
    if (!isConnected) {
      openLoginModal();
      return;
    }
    toast.loading(`Iniciando chamada para ${contactName}...`, {
      id: "voip-call",
    });
    const res = await makeCall({
      phone,
      contactName,
      dealId,
    });
    if (res.ok) {
      toast.success(`Chamando ${contactName} (${phone})`, { id: "voip-call" });
      closeSearch();
    } else {
      toast.error(res.message || "Falha ao iniciar chamada", {
        id: "voip-call",
      });
    }
  };

  const navigateTo = (path: string) => {
    closeSearch();
    navigate(path);
  };

  const formatCurrency = (val?: number) => {
    const num = Number(val) || 0;
    return num.toLocaleString("pt-BR", {
      style: "currency",
      currency: currency || "BRL",
    });
  };

  const totalResultsCount =
    contacts.length + standaloneDeals.length + companies.length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeSearch()}>
      <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-card/95 backdrop-blur-xl border-border shadow-2xl gap-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="sr-only">
          <DialogTitle>Pesquisa Geral do CRM</DialogTitle>
        </DialogHeader>

        {/* Barra Superior / Input de Busca */}
        <div className="p-4 border-b border-border/70 flex items-center gap-3 bg-muted/20">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 text-primary">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </div>
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Digite nome, e-mail, telefone, negócio ou organização..."
            className="h-10 text-sm md:text-base bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-1 placeholder:text-muted-foreground/70"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:inline-flex h-6 select-none items-center gap-1 rounded border border-border bg-muted/60 px-2 font-mono text-[11px] font-medium text-muted-foreground">
            Esc
          </kbd>
        </div>

        {/* Abas de Filtros Rápidos (quando há busca ativa) */}
        {debouncedQuery.length >= 2 && totalResultsCount > 0 && (
          <div className="px-4 py-2 border-b border-border/40 bg-muted/10 flex items-center gap-2 overflow-x-auto text-xs">
            <button
              type="button"
              onClick={() => setActiveTab("all")}
              className={cn(
                "px-2.5 py-1 rounded-full font-medium transition-colors cursor-pointer",
                activeTab === "all"
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              Todos ({totalResultsCount})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("contacts")}
              className={cn(
                "px-2.5 py-1 rounded-full font-medium transition-colors cursor-pointer flex items-center gap-1.5",
                activeTab === "contacts"
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <User className="w-3 h-3" />
              Pessoas ({contacts.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("deals")}
              className={cn(
                "px-2.5 py-1 rounded-full font-medium transition-colors cursor-pointer flex items-center gap-1.5",
                activeTab === "deals"
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <Handshake className="w-3 h-3" />
              Negócios ({standaloneDeals.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("companies")}
              className={cn(
                "px-2.5 py-1 rounded-full font-medium transition-colors cursor-pointer flex items-center gap-1.5",
                activeTab === "companies"
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              <Building2 className="w-3 h-3" />
              Organizações ({companies.length})
            </button>
          </div>
        )}

        {/* Conteúdo Principal / Lista de Resultados */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-[300px]">
          {/* Estado Inicial (sem digitação ou menos de 2 caracteres) */}
          {debouncedQuery.length < 2 && (
            <div className="h-64 flex flex-col items-center justify-center text-center p-6 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                <Sparkles className="w-6 h-6 animate-pulse" />
              </div>
              <h3 className="text-sm font-semibold text-foreground">
                Pesquisa Inteligente & Relacional do CRM
              </h3>
              <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
                Busque por <strong>Nome</strong>, <strong>Telefone</strong> ou{" "}
                <strong>E-mail</strong>. O sistema encontrará a pessoa, sua
                organização e exibirá os{" "}
                <strong>negócios vinculados logo abaixo</strong> dela.
              </p>
            </div>
          )}

          {/* Estado de Carregamento */}
          {isLoading &&
            debouncedQuery.length >= 2 &&
            totalResultsCount === 0 && (
              <div className="h-48 flex flex-col items-center justify-center text-muted-foreground text-xs gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span>Buscando no CRM...</span>
              </div>
            )}

          {/* Nenhum Resultado Encontrado */}
          {!isLoading &&
            debouncedQuery.length >= 2 &&
            totalResultsCount === 0 && (
              <div className="h-56 flex flex-col items-center justify-center text-center p-6 space-y-2">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                  <Search className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  Nenhum resultado para "{debouncedQuery}"
                </p>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Verifique se o número de telefone, e-mail ou nome foram
                  digitados corretamente.
                </p>
              </div>
            )}

          {/* SEÇÃO: PESSOAS / CONTATOS COM NEGÓCIOS VINCULADOS */}
          {(activeTab === "all" || activeTab === "contacts") &&
            contacts.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                  <span className="flex items-center gap-1.5 text-primary">
                    <User className="w-3.5 h-3.5" />
                    Pessoas & Contatos ({contacts.length})
                  </span>
                  <span className="text-[11px] normal-case font-normal text-muted-foreground">
                    Com negócios vinculados
                  </span>
                </div>

                <div className="space-y-3">
                  {contacts.map((contact) => {
                    const fullName =
                      `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
                      "Sem Nome";
                    const initials = fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();

                    return (
                      <div
                        key={`contact-${contact.id}`}
                        className="rounded-xl border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs overflow-hidden group/contact"
                      >
                        {/* Topo do Card da Pessoa */}
                        <div
                          onClick={() =>
                            navigateTo(`/contacts/${contact.id}/show`)
                          }
                          className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-start sm:items-center gap-3 min-w-0">
                            {/* Avatar / Iniciais */}
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center font-bold text-xs shrink-0">
                              {initials}
                            </div>

                            <div className="min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-foreground group-hover/contact:text-primary transition-colors">
                                  {fullName}
                                </span>
                                {contact.matchedField && (
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px] h-4 py-0 px-1.5 font-normal"
                                  >
                                    Match por {contact.matchedField}
                                  </Badge>
                                )}
                                {contact.company_name && (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (contact.company_id) {
                                        navigateTo(
                                          `/companies/${contact.company_id}/show`,
                                        );
                                      }
                                    }}
                                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground bg-muted px-2 py-0.5 rounded-md hover:bg-muted/80 transition-colors"
                                    title="Ver organização"
                                  >
                                    <Building2 className="w-3 h-3 text-muted-foreground" />
                                    <span className="truncate max-w-[140px]">
                                      {contact.company_name}
                                    </span>
                                  </span>
                                )}
                              </div>

                              {/* Informações de Contato (Telefones e Emails) */}
                              <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                                {contact.phones.map((p, idx) => (
                                  <div
                                    key={`p-${idx}`}
                                    className="flex items-center gap-1 font-mono text-[11px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 px-2 py-0.5 rounded"
                                  >
                                    <Phone className="w-2.5 h-2.5" />
                                    <span>{p.number}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => handleCopy(p.number, e)}
                                      className="p-0.5 hover:text-foreground rounded cursor-pointer"
                                      title="Copiar telefone"
                                    >
                                      {copiedText === p.number ? (
                                        <Check className="w-2.5 h-2.5 text-emerald-600" />
                                      ) : (
                                        <Copy className="w-2.5 h-2.5" />
                                      )}
                                    </button>
                                    {(isVoipEnabled || isSuperadmin) && (
                                      <button
                                        type="button"
                                        onClick={(e) =>
                                          handleCall(
                                            p.number,
                                            fullName,
                                            undefined,
                                            e,
                                          )
                                        }
                                        className="p-0.5 text-emerald-600 hover:text-emerald-700 hover:scale-110 transition-transform cursor-pointer"
                                        title="Ligar agora via VoIP"
                                      >
                                        <PhoneCall className="w-2.5 h-2.5" />
                                      </button>
                                    )}
                                  </div>
                                ))}

                                {contact.emails.map((em, idx) => (
                                  <div
                                    key={`em-${idx}`}
                                    className="flex items-center gap-1 text-[11px] text-muted-foreground bg-muted/60 px-2 py-0.5 rounded"
                                  >
                                    <Mail className="w-2.5 h-2.5" />
                                    <span className="truncate max-w-[170px]">
                                      {em.email}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => handleCopy(em.email, e)}
                                      className="p-0.5 hover:text-foreground rounded cursor-pointer"
                                      title="Copiar e-mail"
                                    >
                                      {copiedText === em.email ? (
                                        <Check className="w-2.5 h-2.5 text-emerald-600" />
                                      ) : (
                                        <Copy className="w-2.5 h-2.5" />
                                      )}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Ações da Pessoa */}
                          <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                navigateTo(`/contacts/${contact.id}/show`)
                              }
                            >
                              <span>Ver contato</span>
                              <ArrowRight className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Bloco de Negócios Vinculados Logo Abaixo da Pessoa */}
                        <div className="border-t border-border/60 bg-muted/15 px-3.5 py-2.5">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                              <Handshake className="w-3 h-3 text-primary" />
                              Negócios desta pessoa ({contact.deals.length})
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                navigateTo(
                                  `/deals/create?contact_ids=${contact.id}${
                                    contact.company_id
                                      ? `&company_id=${contact.company_id}`
                                      : ""
                                  }`,
                                )
                              }
                              className="text-[11px] text-primary hover:underline flex items-center gap-1 font-medium cursor-pointer"
                            >
                              <Plus className="w-3 h-3" />
                              Novo Negócio
                            </button>
                          </div>

                          {contact.deals.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {contact.deals.map((deal) => {
                                const stageLabel = findDealLabel(
                                  dealStages,
                                  deal.stage || "",
                                );
                                const isWon =
                                  deal.status === "ganho" ||
                                  deal.stage === "won";
                                const isLost =
                                  deal.status === "perdido" ||
                                  deal.stage === "lost";

                                return (
                                  <div
                                    key={`contact-deal-${deal.id}`}
                                    onClick={() =>
                                      navigateTo(`/deals/${deal.id}/show`)
                                    }
                                    className="flex items-center justify-between p-2 rounded-lg border border-border/70 bg-card hover:bg-muted/40 hover:border-primary/40 transition-all cursor-pointer group/deal"
                                  >
                                    <div className="min-w-0 pr-2">
                                      <div className="flex items-center gap-1.5">
                                        <Briefcase className="w-3 h-3 text-muted-foreground group-hover/deal:text-primary transition-colors shrink-0" />
                                        <span className="text-xs font-medium text-foreground truncate group-hover/deal:text-primary transition-colors">
                                          {deal.name}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                          {formatCurrency(deal.amount)}
                                        </span>
                                        {deal.company_name && (
                                          <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">
                                            • {deal.company_name}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "text-[10px] shrink-0 font-normal",
                                        isWon &&
                                          "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
                                        isLost &&
                                          "bg-rose-500/10 text-rose-600 border-rose-500/30",
                                        !isWon &&
                                          !isLost &&
                                          "bg-blue-500/10 text-blue-600 border-blue-500/30",
                                      )}
                                    >
                                      {stageLabel || deal.stage || "Etapa"}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="py-2 text-center text-xs text-muted-foreground italic flex items-center justify-center gap-1.5">
                              <span>
                                Nenhum negócio ativo vinculado a esta pessoa.
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* SEÇÃO: ORGANIZAÇÕES / EMPRESAS */}
          {(activeTab === "all" || activeTab === "companies") &&
            companies.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                  <span className="flex items-center gap-1.5 text-primary">
                    <Building2 className="w-3.5 h-3.5" />
                    Organizações ({companies.length})
                  </span>
                </div>

                <div className="space-y-2.5">
                  {companies.map((comp) => (
                    <div
                      key={`company-${comp.id}`}
                      onClick={() => navigateTo(`/companies/${comp.id}/show`)}
                      className="p-3.5 rounded-xl border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs cursor-pointer group/comp flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-600 border border-blue-500/20 flex items-center justify-center shrink-0 font-semibold text-xs">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground group-hover/comp:text-primary transition-colors">
                              {comp.name}
                            </span>
                            {comp.sector && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] h-4 py-0 px-1.5 font-normal"
                              >
                                {comp.sector}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            {comp.phone_number && (
                              <span className="flex items-center gap-1 font-mono text-[11px]">
                                <Phone className="w-2.5 h-2.5 text-emerald-600" />
                                {comp.phone_number}
                              </span>
                            )}
                            {comp.city && (
                              <span>
                                {comp.city}
                                {comp.state_abbr ? ` - ${comp.state_abbr}` : ""}
                              </span>
                            )}
                            {comp.website && (
                              <span className="text-primary truncate max-w-[150px]">
                                {comp.website}
                              </span>
                            )}
                            <span className="text-[11px]">
                              • {comp.contacts.length} contatos •{" "}
                              {comp.deals.length} negócios
                            </span>
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground shrink-0 self-end sm:self-center"
                      >
                        <span>Ver empresa</span>
                        <ArrowRight className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* SEÇÃO: NEGÓCIOS ENCONTRADOS DIRETAMENTE PELO TÍTULO */}
          {(activeTab === "all" || activeTab === "deals") &&
            standaloneDeals.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                  <span className="flex items-center gap-1.5 text-primary">
                    <Handshake className="w-3.5 h-3.5" />
                    Negócios ({standaloneDeals.length})
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {standaloneDeals.map((deal) => {
                    const stageLabel = findDealLabel(
                      dealStages,
                      deal.stage || "",
                    );
                    const isWon =
                      deal.status === "ganho" || deal.stage === "won";
                    const isLost =
                      deal.status === "perdido" || deal.stage === "lost";

                    return (
                      <div
                        key={`deal-${deal.id}`}
                        onClick={() => navigateTo(`/deals/${deal.id}/show`)}
                        className="p-3 rounded-xl border border-border/80 bg-card hover:border-primary/40 hover:bg-muted/30 transition-all shadow-xs cursor-pointer group/d flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-1.5">
                            <Handshake className="w-3.5 h-3.5 text-primary shrink-0" />
                            <span className="text-xs font-semibold text-foreground truncate group-hover/d:text-primary transition-colors">
                              {deal.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(deal.amount)}
                            </span>
                            {deal.company_name && (
                              <span className="text-[11px] text-muted-foreground truncate max-w-[130px]">
                                • {deal.company_name}
                              </span>
                            )}
                          </div>
                        </div>

                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] shrink-0 font-normal",
                            isWon &&
                              "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
                            isLost &&
                              "bg-rose-500/10 text-rose-600 border-rose-500/30",
                            !isWon &&
                              !isLost &&
                              "bg-blue-500/10 text-blue-600 border-blue-500/30",
                          )}
                        >
                          {stageLabel || deal.stage || "Etapa"}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* Rodapé com Dicas de Teclado */}
        <div className="p-2.5 px-4 border-t border-border/60 bg-muted/30 flex items-center justify-between text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>
              Dica: Digite apenas os números do telefone (ex: 4599969) para
              busca exata.
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <span>[Esc] para fechar</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
